// A company's logo, or its initials when it has none (owner 2026-10-11:
// "make sure every company logo file is visible when they have an account").
// One component for the header, the company picker and the admin company
// list, so the three can never disagree about what a company looks like.
//
// Company.companyLogo is a storage key (`<uuid>.png.gz`, see
// back/utils/companyLogo.js); helpers/extractImageFromZip turns it into an
// object URL. That is a network fetch, and the header re-creates its inner
// component on every render (Header.jsx HeaderComp), so a plain
// useEffect-per-mount would fetch the logo again on each re-render and
// flash the initials in between. The result is therefore kept per key for
// the life of the page: one fetch per logo, and a remount paints the image
// straight away.
//
// A logo that is set but cannot be loaded (the file is gone from storage)
// is not hidden behind the initials silently: `showMissing` (the admin
// list) says so, and every caller gets a tooltip naming the problem.
import React, { useEffect, useState } from "react";

import { extractImageFromZip } from "helpers/extractImageFromZip";

export const companyInitials = (name) =>
  String(name || "")
    .replace(/_\d{10,}$/, "") // registration appends _<timestamp> to the name
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z0-9\u00C0-\uFFFF]/g, ""))
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

// key -> object URL | null (null = set but could not be loaded)
const resolved = new Map();
const pending = new Map();

export const loadCompanyLogo = (logo) => {
  if (!logo) return Promise.resolve(null);
  if (resolved.has(logo)) return Promise.resolve(resolved.get(logo));
  if (!pending.has(logo)) {
    pending.set(
      logo,
      Promise.resolve(extractImageFromZip(logo))
        .catch(() => null)
        .then((src) => {
          resolved.set(logo, src || null);
          pending.delete(logo);
          return src || null;
        })
    );
  }
  return pending.get(logo);
};

// Tests only.
export const resetCompanyLogoCache = () => {
  resolved.clear();
  pending.clear();
};

// state: "none" (no logo set), "loading", "ok", "missing" (set, not loadable)
const stateFor = (logo) => {
  if (!logo) return { state: "none", src: null };
  if (!resolved.has(logo)) return { state: "loading", src: null };
  const src = resolved.get(logo);
  return src ? { state: "ok", src } : { state: "missing", src: null };
};

const CompanyLogo = ({ logo, name, size = 48, radius, showMissing = false, style }) => {
  const [view, setView] = useState(() => stateFor(logo));

  useEffect(() => {
    let alive = true;
    setView(stateFor(logo));
    if (logo && !resolved.has(logo)) {
      loadCompanyLogo(logo).then(() => {
        if (alive) setView(stateFor(logo));
      });
    }
    return () => {
      alive = false;
    };
  }, [logo]);

  const box = {
    width: size,
    height: size,
    borderRadius: radius == null ? Math.round(size / 6) : radius,
    flex: `0 0 ${size}px`,
    ...style,
  };

  if (view.state === "ok") {
    return (
      <img
        src={view.src}
        alt={name ? `${name} logo` : "Company logo"}
        title={name || ""}
        data-testid="company-logo-img"
        style={{ ...box, objectFit: "contain", background: "#fff", border: "1px solid #e5e5e5" }}
        onError={() => {
          resolved.set(logo, null);
          setView({ state: "missing", src: null });
        }}
      />
    );
  }

  const missing = view.state === "missing";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        data-testid="company-logo-initials"
        title={missing ? "The logo file for this company could not be found. Set the logo again." : name || ""}
        style={{
          ...box,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#3A5765",
          color: "#fff",
          fontWeight: 600,
          fontSize: Math.max(10, Math.round(size * 0.36)),
          lineHeight: 1,
        }}
      >
        {companyInitials(name)}
      </span>
      {missing && showMissing ? (
        <span className="badge badge-warning" data-testid="company-logo-missing">
          Logo file missing
        </span>
      ) : null}
    </span>
  );
};

export default CompanyLogo;
